import { useCallback, useEffect, useState } from 'react';
import { type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Cross2Icon, VideoIcon } from '@radix-ui/react-icons';
import { toast } from 'sonner';
import { FormSection } from '@/shared/ui/components/form';
import { TextInput } from '@/shared/ui/components/form/inputs';
import { Button, Text, UnstyledButton } from '@/shared/ui';
import { PropertiesService, type PropertyVideoFile } from '@/services/requests/housien-services/PropertiesService';
import { uploadPropertyVideo } from '@/services/videos-upload';
import {
  ACCEPTED_VIDEO_TYPES,
  MAX_PROPERTY_VIDEOS,
  parseVideoLink,
  videoFileProblem,
} from '@/shared/utils/property-videos';
import type { PropertyFormValues } from './schema';

interface VideoSectionProps {
  form: UseFormReturn<PropertyFormValues>;
  agencyId: string;
  /** Absent while creating: files need a saved property to live under. */
  propertyId?: string;
}

interface PendingUpload {
  key: string;
  name: string;
  progress: number;
}

type LinkProblem = 'invalid' | 'duplicate' | 'limit';

/**
 * Why *draft* cannot be added to *links*, or null when it can.
 */
function linkProblem(draft: string, links: string[], full: boolean): LinkProblem | null {
  if (!parseVideoLink(draft)) return 'invalid';
  if (links.includes(draft)) return 'duplicate';
  return full ? 'limit' : null;
}

/**
 * Reads the HTTP status off an ApiError-shaped rejection.
 */
function statusOf(error: unknown): number | undefined {
  return (error as { status?: number })?.status;
}

const rowClass = 'flex items-center gap-(--space-3) py-(--space-2) border-b border-(--border-subtle) last:border-b-0';
const removeClass = 'shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-(--text-tertiary) hover:bg-(--surface-hover) hover:text-(--text-primary)';

/**
 * Loads and edits the uploaded video files of a saved property.
 */
function useVideoFiles(agencyId: string, propertyId: string | undefined) {
  const [files, setFiles] = useState<PropertyVideoFile[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!propertyId) return;
    const res = await PropertiesService.getPropertyVideos(agencyId, propertyId);
    setFiles(res.videos ?? []);
  }, [agencyId, propertyId]);

  useEffect(() => {
    setLoading(true);
    refresh().catch(() => setFiles([])).finally(() => setLoading(false));
  }, [refresh]);

  return { files, setFiles, loading, refresh };
}

/**
 * Uploads picked files one by one, tracking each one's progress.
 */
function useVideoUploads(agencyId: string, propertyId: string | undefined, onUploaded: () => Promise<void>) {
  const { t } = useTranslation('features/properties');
  const [uploads, setUploads] = useState<PendingUpload[]>([]);

  const setProgress = (key: string, progress: number) =>
    setUploads((prev) => prev.map((u) => (u.key === key ? { ...u, progress } : u)));

  const uploadOne = useCallback(async (file: File) => {
    if (!propertyId) return;
    const key = `${file.name}-${Date.now()}`;
    setUploads((prev) => [...prev, { key, name: file.name, progress: 0 }]);
    try {
      await uploadPropertyVideo(agencyId, propertyId, file, (p) => setProgress(key, p));
      await onUploaded();
    } catch (error) {
      toast.error(t(statusOf(error) === 409 ? 'propertyDrawer.videos.limitReached' : 'propertyDrawer.videos.uploadFailed', { name: file.name, max: MAX_PROPERTY_VIDEOS }));
    } finally {
      setUploads((prev) => prev.filter((u) => u.key !== key));
    }
  }, [agencyId, propertyId, onUploaded, t]);

  return { uploads, uploadOne };
}

/**
 * The drawer's video section: YouTube and Vimeo links saved with the form,
 * and video files uploaded straight away to a saved property. Both count
 * toward one limit of five per property.
 */
export function VideoSection({ form, agencyId, propertyId }: VideoSectionProps) {
  const { t } = useTranslation('features/properties');
  const links = form.watch('video_links') ?? [];
  const [draft, setDraft] = useState('');
  const [problem, setProblem] = useState<LinkProblem | null>(null);
  const { files, setFiles, loading, refresh } = useVideoFiles(agencyId, propertyId);
  const { uploads, uploadOne } = useVideoUploads(agencyId, propertyId, refresh);

  const total = links.length + files.length + uploads.length;
  const full = total >= MAX_PROPERTY_VIDEOS;

  const setLinks = (next: string[]) => form.setValue('video_links', next, { shouldDirty: true });

  const addLink = () => {
    const value = draft.trim();
    const found = linkProblem(value, links, full);
    setProblem(found);
    if (found) return;
    setLinks([...links, value]);
    setDraft('');
  };

  const pickFiles = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = ACCEPTED_VIDEO_TYPES.join(',');
    input.onchange = () => startUploads(Array.from(input.files ?? []));
    input.click();
  };

  const startUploads = (picked: File[]) => {
    const room = MAX_PROPERTY_VIDEOS - total;
    if (picked.length > room) toast.error(t('propertyDrawer.videos.limitReached', { max: MAX_PROPERTY_VIDEOS }));
    picked.slice(0, Math.max(room, 0)).forEach((file) => {
      const fileProblem = videoFileProblem(file);
      if (fileProblem) toast.error(t(`propertyDrawer.videos.file.${fileProblem}`, { name: file.name }));
      else void uploadOne(file);
    });
  };

  const removeFile = (video: PropertyVideoFile) => {
    if (!propertyId) return;
    setFiles((prev) => prev.filter((v) => v.id !== video.id));
    PropertiesService.deletePropertyVideo(agencyId, propertyId, video.id).catch(() => {
      setFiles((prev) => [...prev, video]);
      toast.error(t('propertyDrawer.videos.deleteFailed'));
    });
  };

  return (
    <FormSection title={t('propertyDrawer.videos.title')}>
      <Text variant="meta" className="m-0 text-(--text-tertiary)" data-testid="drawer-videos-count">
        {t('propertyDrawer.videos.count', { count: total, max: MAX_PROPERTY_VIDEOS })}
      </Text>

      <div className="flex items-end gap-(--space-2)">
        <div className="flex-1 min-w-0">
          <TextInput
            label={t('propertyDrawer.videos.linkLabel')}
            placeholder={t('propertyDrawer.videos.linkPlaceholder')}
            value={draft}
            error={problem ? t(`propertyDrawer.videos.linkProblem.${problem}`, { max: MAX_PROPERTY_VIDEOS }) : undefined}
            onChange={(e) => { setDraft(e.target.value); setProblem(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addLink(); } }}
            data-testid="drawer-video-link-input"
          />
        </div>
        <Button
          type="button"
          variant="outlined"
          disabled={!draft.trim()}
          onClick={addLink}
          className={problem ? 'mb-6' : undefined}
          data-testid="drawer-video-link-add"
        >
          {t('propertyDrawer.videos.addLink')}
        </Button>
      </div>

      {(links.length > 0 || files.length > 0 || uploads.length > 0) && (
        <div data-testid="drawer-videos-list">
          {links.map((link) => (
            <div key={link} className={rowClass}>
              <VideoIcon className="shrink-0 text-(--text-tertiary)" />
              <Text as="span" variant="bodySm" className="flex-1 min-w-0 break-all text-(--text-primary)">
                {link}
              </Text>
              <UnstyledButton
                type="button"
                aria-label={t('propertyDrawer.videos.remove')}
                onClick={() => setLinks(links.filter((l) => l !== link))}
                className={removeClass}
              >
                <Cross2Icon />
              </UnstyledButton>
            </div>
          ))}
          {files.map((video, i) => (
            <div key={video.id} className={rowClass}>
              <video
                src={video.url}
                preload="metadata"
                muted
                className="shrink-0 w-24 aspect-video rounded-(--radius-sm) bg-(--surface-tertiary) object-cover"
              />
              <Text as="span" variant="bodySm" className="flex-1 min-w-0 text-(--text-primary)">
                {t('propertyDrawer.videos.fileLabel', { index: i + 1 })}
              </Text>
              <UnstyledButton
                type="button"
                aria-label={t('propertyDrawer.videos.remove')}
                onClick={() => removeFile(video)}
                className={removeClass}
              >
                <Cross2Icon />
              </UnstyledButton>
            </div>
          ))}
          {uploads.map((upload) => (
            <div key={upload.key} className={rowClass}>
              <div className="flex-1 min-w-0 flex flex-col gap-(--space-1)">
                <Text as="span" variant="bodySm" className="break-all text-(--text-primary)">
                  {t('propertyDrawer.videos.uploading', { name: upload.name, percent: Math.round(upload.progress * 100) })}
                </Text>
                <div className="h-1.5 rounded-full bg-(--surface-tertiary) overflow-hidden">
                  <div
                    className="h-full bg-(--seed-base) transition-[width] duration-200"
                    style={{ width: `${Math.round(upload.progress * 100)}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {propertyId ? (
        <Button
          type="button"
          variant="outlined"
          disabled={full || loading}
          onClick={pickFiles}
          leftSection={<VideoIcon />}
          data-testid="drawer-video-upload"
        >
          {t('propertyDrawer.videos.uploadFile')}
        </Button>
      ) : (
        <Text variant="meta" className="m-0 text-(--text-tertiary)">
          {t('propertyDrawer.videos.saveFirst')}
        </Text>
      )}
      {propertyId && (
        <Text variant="meta" className="m-0 text-(--text-tertiary)">
          {t('propertyDrawer.videos.fileHint')}
        </Text>
      )}
    </FormSection>
  );
}
